import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { ChooseModality as ChooseModalityComponent } from '@imprivata/nucleus';
import * as actions from '../../actions';
import { connect } from 'react-redux';
import helpIcon from '../../images/telephone.svg';
import iidIcon from '../../images/phone.svg';
import otpIcon from '../../images/otp.svg';
import smsIcon from '../../images/SMSIcon.svg';
import tempCodeIcon from '../../images/tempIcon.svg';
import emailIcon from '../../images/email.svg';
import passwordIcon from '../../images/password.svg';
import pinIcon from '../../images/pin.svg';
import faceIcon from '../../images/face.svg';
import badgeIcon from '../../images/badge.svg';
import idvIcon from '../../images/idv.svg';
import LogoutStyled from '../shared/LogoutStyled';
import { getModalityOrder } from '../../constants/workflowConstants';

import './ChooseModality.scss';

/**
 * Displays a waiting screen while the user accepts the push on their mobile device.
 *
 * @export
 * @class ChooseModality
 * @extends {Component}
 */
export class ChooseModality extends Component {
  /**
   * The allowed properties for the ChooseModality component
   * @param {array} availableModalities available modalities to switch to
   */
  static propTypes = {
    availableModalities: PropTypes.arrayOf(PropTypes.string),
    lastFourDigits: PropTypes.string,
    changeModality: PropTypes.func,
    modality: PropTypes.oneOf([
      'sms',
      'otp',
      'iid',
      'iid-manual',
      'tempcode',
      'password',
      'pin',
      'email',
      'idv',
      'proxcard',
    ]),
    moreOptionsChooseModality: PropTypes.bool,
    /** Use Parkland custom UX */
    useCustomUX: PropTypes.bool,
  };
  static defaultProps = {
    availableModalities: [],
    lastFourDigits: 'XXXX',
    modality: 'iid',
    moreOptionsChooseModality: false,
    useCustomUX: false,
  };

  buildModalityObject = (modalityName) => {
    let modality = { link: () => this.props.changeModality(modality, modalityName), };

    switch (modalityName) {
    case 'face':
      modality.id = 'btnFace';
      modality.title = 'Face recognition';
      modality.icon = faceIcon;
      modality.iconClass = 'modality-svg';
      modality.onClickFunction = () => this.props.changeModality(this.props.currentModality, 'face');
      modality.description = 'Scan your face';
      break;
    case 'proxcard':
      modality.id = 'btnProxCard';
      modality.title = 'Proximity Card';
      modality.icon = badgeIcon;
      modality.iconClass = 'modality-svg';
      modality.onClickFunction = () => this.props.changeModality(this.props.currentModality, 'proxcard');
      modality.description = 'Tap your proximity card';
      break;
    case 'password':
      modality.id = 'btnPassword';
      modality.title = 'Password';
      modality.icon = passwordIcon;
      modality.iconClass = 'modality-svg';
      modality.onClickFunction = () => this.props.changeModality(this.props.currentModality, 'password');
      modality.description = 'Enter your password';
      break;
    case 'pin':
      modality.id = 'btnPin';
      modality.title = 'Imprivata PIN';
      modality.icon = pinIcon;
      modality.iconClass = 'modality-svg';
      modality.onClickFunction = () => this.props.changeModality(this.props.currentModality, 'pin');
      modality.description = 'Enter your Imprivata PIN';
      break;
    case 'iid':
      modality.id = 'iid';
      modality.title = 'Imprivata ID';
      modality.icon = iidIcon;
      modality.iconClass = 'modality-svg';
      modality.onClickFunction = () => this.props.changeModality(this.props.currentModality, 'iid');
      modality.description = 'Approve a push notification';
      break;
    case 'sms':
      modality.id = 'btnSms';
      modality.title = 'SMS code';
      modality.icon = smsIcon;
      modality.iconClass = '';
      modality.onClickFunction = () => this.props.changeModality(this.props.currentModality, 'sms');
      modality.description = '';
      break;
    case 'tempcode':
      modality.id = 'btnTempcode';
      modality.title = 'Temporary code';
      modality.icon = tempCodeIcon;
      modality.iconClass = '';
      modality.onClickFunction = () => this.props.changeModality(this.props.currentModality, 'tempcode');
      modality.description = 'Enter the code provided by your help desk';
      break;
    case 'otp':
      modality.id = 'btnOtp';
      modality.title = 'Token code';
      modality.icon = otpIcon;
      modality.iconClass = 'modality-svg';
      modality.onClickFunction = () => this.props.changeModality(this.props.currentModality, 'otp');
      modality.description = '';
      break;
    case 'email':
      modality.id = 'btnEmail';
      modality.title = 'Email';
      modality.icon = emailIcon;
      modality.iconClass = 'modality-svg';
      modality.onClickFunction = () => this.props.changeModality(this.props.currentModality, 'email');
      modality.description = 'Enter the code sent to your email';
      break;
    case 'idv':
      modality.id = 'btnIdv';
      modality.title = 'Verify your identity';
      modality.icon = idvIcon;
      modality.iconClass = 'modality-svg';
      modality.onClickFunction = () => this.props.changeModality(this.props.currentModality, 'idv');
      modality.description = 'Use your government ID (drivers license) to verify';
      break;
    case 'helpDesk':
      modality.id = 'btnHelp';
      modality.title = 'Still stuck?';
      modality.icon = helpIcon;
      modality.iconClass = 'modality-svg';
      modality.description = 'Call your help desk for a temporary code';
      modality.link = null;
      break;
    default:
      modality.id = 'btnUnknown';
      modality.title = 'Unknown';
      modality.icon = tempCodeIcon;
      modality.iconClass = '';
      modality.description = <span>Modality not mapped.</span>;
      modality.link = null;
    }

    // Add order to modality object so we can sort them later
    modality.order = getModalityOrder(modalityName);
    return modality;
  };

  removeModalities = (modalities, modalitiesToRemove) => {
    return modalities.filter(e => !modalitiesToRemove.includes(e) );
  };

  // Add HelpDesk if temporary codes are allowed but a temporary code is not available to user
  shouldDisplayHelpDesk = () => {
    if (this.props.serverState !== undefined &&
      this.props.serverState.temporaryCodeAllowed !== undefined &&
      this.props.serverState.temporaryCodeAvailable !== undefined &&
      this.props.serverState.temporaryCodeAllowed &&
      !this.props.serverState.temporaryCodeAvailable &&
      // show Helpdesk item only if user has at least one authenticated modality
      this.props.authenticatedModalities.length > 0) {
      return true;
    }
    return false;
  };

  setModalityAction = (defaultModality) => {
    this.props.changeModality(defaultModality, defaultModality);
  };

  render() {
    const {
      availableModalities,
      modality,
      moreOptionsChooseModality,
      useCustomUX,
    } = this.props;

    const moreOptionsMode = moreOptionsChooseModality;
    const defaultModality = 'iid';
    const modalitiesAvailable = (availableModalities) ? availableModalities : [];

    // If current modality is 'iid-manual' then treat it as the 'iid' modality.
    const currentModality = (modality === 'iid-manual' ? 'iid' : modality);
    const modalitiesToRemove = [];
    modalitiesToRemove.push( (moreOptionsMode) ? currentModality : defaultModality);
    if (currentModality === 'face' || currentModality === 'password'
      || currentModality === 'pin' || currentModality === 'proxcard') {
      modalitiesToRemove.push('tempcode');
    }

    let modalities = this.removeModalities(modalitiesAvailable, modalitiesToRemove);
    (this.shouldDisplayHelpDesk() ) && modalities.push('helpDesk');

    // builds an array of modalities sorted by the {modality.order} attribute
    const modalitiesToRender = (modalities.map(this.buildModalityObject) ).sort( (a, b) => a.order - b.order);
    const logoutStyle = useCustomUX ? 'black' : 'white';

    if (moreOptionsMode === true) {
      return (
        <div className='content-body'>
          <ChooseModalityComponent
            showDefaultModality={false}
            modalities={modalitiesToRender}
            logoutComponent={<LogoutStyled type={logoutStyle} />}
            showDescriptions={true}
            headerTitle='Choose another method'
            headerDescription=''
            useCustomUX={useCustomUX}
          />
        </div>
      );
    }
    else {
      return (
        <div className='content-body'>
          <ChooseModalityComponent
            showDefaultModality={modalitiesAvailable.includes(defaultModality)}
            modalities={modalitiesToRender}
            defaultModalityAction={() => this.setModalityAction(defaultModality)}
            defaultModalityIcon={iidIcon}
            logoutComponent={<LogoutStyled type={logoutStyle} />}
            headerTitle='Verify your identity'
            headerDescription='To keep your logins secure, choose a way to verify your identity before enrolling a new device'
            useCustomUX={useCustomUX}
          />
        </div>
      );
    }
  }
}


const mapStateToProps = (state) => {
  const {
    availableModalities, lastFourDigits, modality, moreOptionsChooseModality, serverState, useCustomUX, authenticatedModalities,
  } = state.login;
  return {
    availableModalities,
    lastFourDigits,
    modality,
    moreOptionsChooseModality,
    serverState,
    useCustomUX,
    authenticatedModalities,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    changeModality: (fromModality, toModality) => {
      dispatch(actions.endIgnoreTimeout() );
      dispatch(actions.changeModality(fromModality, toModality) );
    },
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(ChooseModality);
